import React from 'react';
import { Box, Group, Text, ThemeIcon, UnstyledButton } from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';
import { SCENE_TEMPLATES, type SceneTemplateId } from '../../data/ShellConfig';
import SceneTemplateHint from './SceneTemplateHint';

type SceneTemplatePickerProps = {
    templateId: SceneTemplateId;
    onChange: (templateId: SceneTemplateId) => void;
};

const TILE_WIDTH = 72;
const THUMB_HEIGHT = 96;

/**
 * Template chooser for a scene, shared by the drawer form and the Studio canvas
 * editor so the two can't drift apart.
 *
 * Selection is signalled four ways over — ring, tint, check badge, and by
 * fading the options you didn't pick — because a single blue border on a tile
 * whose thumbnail is already blue read as almost nothing.
 */
const SceneTemplatePicker: React.FC<SceneTemplatePickerProps> = ({ templateId, onChange }) => (
    <div>
        <Text size="sm" fw={600} mb="xs">
            Scene Template
        </Text>
        <Group gap="sm" align="stretch">
            {SCENE_TEMPLATES.map(template => {
                const selected = templateId === template.id;
                return (
                    <UnstyledButton
                        key={template.id}
                        onClick={() => onChange(template.id)}
                        aria-pressed={selected}
                        aria-label={`${template.label} template`}
                        style={{
                            width: TILE_WIDTH,
                            borderRadius: 8,
                            border: `1px solid ${
                                selected
                                    ? 'var(--mantine-color-blue-6)'
                                    : 'var(--mantine-color-gray-4)'
                            }`,
                            // The selection ring is a shadow, not a thicker border, so
                            // picking a tile can't change its size or shift its neighbours.
                            boxShadow: selected ? '0 0 0 2px var(--mantine-color-blue-6)' : undefined,
                            background: selected
                                ? 'var(--mantine-color-blue-light)'
                                : 'var(--mantine-color-body)',
                            padding: 6,
                            display: 'flex',
                            flexDirection: 'column',
                            position: 'relative',
                            transition: 'background 120ms ease, box-shadow 120ms ease',
                        }}
                    >
                        {selected && (
                            <ThemeIcon
                                size={20}
                                radius="xl"
                                color="blue"
                                style={{
                                    position: 'absolute',
                                    top: -8,
                                    right: -8,
                                    border: '2px solid var(--mantine-color-body)',
                                }}
                            >
                                <IconCheck size={12} stroke={3} />
                            </ThemeIcon>
                        )}
                        <Box
                            style={{
                                height: THUMB_HEIGHT,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2,
                                background: '#111',
                                borderRadius: 4,
                                overflow: 'hidden',
                                // Unpicked options recede rather than competing with the
                                // one that's active.
                                opacity: selected ? 1 : 0.45,
                                transition: 'opacity 120ms ease',
                            }}
                        >
                            {template.panels.map((h, i) => (
                                <Box
                                    key={i}
                                    style={{
                                        height: `${h}%`,
                                        background:
                                            i % 2 === 0
                                                ? 'var(--mantine-color-blue-6)'
                                                : 'var(--mantine-color-violet-6)',
                                        opacity: 0.85,
                                    }}
                                />
                            ))}
                        </Box>
                        {/* Two lines are reserved so one-line labels ("20 / 80") stay the
                            same height as wrapping ones. */}
                        <Text
                            size="xs"
                            ta="center"
                            mt={4}
                            lh={1.2}
                            fw={selected ? 700 : 400}
                            c={selected ? 'blue' : 'dimmed'}
                            style={{ minHeight: 29 }}
                        >
                            {template.label}
                        </Text>
                    </UnstyledButton>
                );
            })}
        </Group>
        <SceneTemplateHint templateId={templateId} />
    </div>
);

export default SceneTemplatePicker;
